/**
 * Traduz mensagens de erro do Supabase Auth (em inglês) para pt-BR.
 * Também cobre mensagens de validação de senha (HIBP, tamanho, etc.)
 * e erros comuns de login/cadastro/recuperação.
 */
const PATTERNS: Array<{ match: RegExp; message: string }> = [
  { match: /password is known to be weak|pwned|compromised password/i, message: 'Essa senha é muito comum e fácil de adivinhar. Escolha uma senha mais forte.' },
  { match: /password should be at least (\d+)/i, message: 'A senha deve ter no mínimo 6 caracteres.' },
  { match: /password.*too short/i, message: 'A senha é muito curta. Use pelo menos 6 caracteres.' },
  { match: /password.*should contain/i, message: 'A senha não atende aos requisitos mínimos de segurança.' },
  { match: /invalid login credentials|invalid.*email.*password/i, message: 'E-mail ou senha incorretos.' },
  { match: /email not confirmed/i, message: 'E-mail ainda não confirmado. Verifique sua caixa de entrada.' },
  { match: /user already registered|already.*registered/i, message: 'Este e-mail já está cadastrado.' },
  { match: /user not found/i, message: 'Usuário não encontrado.' },
  { match: /invalid email/i, message: 'E-mail inválido.' },
  { match: /email rate limit exceeded|over_email_send_rate_limit/i, message: 'Muitos e-mails enviados. Aguarde alguns minutos antes de tentar novamente.' },
  { match: /rate limit|too many requests/i, message: 'Muitas tentativas. Aguarde um momento e tente novamente.' },
  { match: /token has expired|otp.*expired|link.*expired/i, message: 'O link expirou. Solicite um novo.' },
  { match: /invalid.*token|token.*invalid|invalid.*otp/i, message: 'Link inválido ou já utilizado. Solicite um novo.' },
  { match: /new password should be different/i, message: 'A nova senha deve ser diferente da senha atual.' },
  { match: /same as the old password/i, message: 'A nova senha deve ser diferente da senha atual.' },
  { match: /session (missing|not found|expired)|jwt expired|refresh token/i, message: 'Sua sessão expirou. Faça login novamente.' },
  { match: /signup.*disabled|signups not allowed/i, message: 'Novos cadastros estão desativados no momento.' },
  { match: /network|failed to fetch/i, message: 'Falha de conexão. Verifique sua internet e tente novamente.' },
];

const CODE_MESSAGES: Record<string, string> = {
  invalid_credentials: 'E-mail ou senha incorretos.',
  invalid_grant: 'E-mail ou senha incorretos.',
  email_not_confirmed: 'E-mail ainda não confirmado. Verifique sua caixa de entrada.',
  user_not_found: 'E-mail ou senha incorretos.',
  user_banned: 'Esta conta está temporariamente bloqueada.',
  over_request_rate_limit: 'Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.',
  over_email_send_rate_limit: 'Muitos e-mails enviados. Aguarde alguns minutos antes de tentar novamente.',
  weak_password: 'Essa senha não atende aos requisitos de segurança.',
  same_password: 'A nova senha deve ser diferente da senha atual.',
  user_already_exists: 'Este e-mail já está cadastrado.',
  email_exists: 'Este e-mail já está cadastrado.',
  signup_disabled: 'Novos cadastros estão desativados no momento.',
};

export function translateAuthError(input: unknown, fallback = 'Ocorreu um erro. Tente novamente.'): string {
  const obj = input && typeof input === 'object' ? (input as Record<string, unknown>) : null;
  const code = obj && typeof obj.code === 'string' ? obj.code.toLowerCase() : '';
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];

  const parts =
    typeof input === 'string'
      ? [input]
      : obj
        ? [obj.message, obj.error_description, obj.error, obj.msg, obj.details].filter(
            (v): v is string => typeof v === 'string' && v.length > 0,
          )
        : [];
  const raw = parts.join(' ');

  if (/invalid_grant|invalid_credentials|invalid credentials/i.test(raw)) return 'E-mail ou senha incorretos.';
  if (/load failed|networkerror|network request failed/i.test(raw)) {
    return 'Falha de conexão. Verifique sua internet e tente novamente.';
  }
  if (obj && obj.status === 429) return 'Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.';

  if (!raw) return fallback;

  for (const { match, message } of PATTERNS) {
    if (match.test(raw)) return message;
  }

  // Se a mensagem já parece estar em português, retorna como está.
  if (/[áéíóúãõâêôç]/i.test(raw) || /\b(senha|e-?mail|usuário|conta|inválido|expirou)\b/i.test(raw)) {
    return raw;
  }

  return fallback;
}
